import { date } from 'quasar'
import { serverUri } from 'src/config'
import Studio from 'src/models/box-office-report/Studio'
import Model, { encoders, executeMutation, GqlError, ID, Integer, IsoDate, IsoDateTime, MODEL_LEVEL } from 'src/models/Model'
import Movie from 'src/models/movie/Movie'
import MovieTerm from 'src/models/movie/MovieTerm'
import MovieTermWeek from 'src/models/movie/MovieTermWeek'
import BoxOfficeReportSettlement from 'src/models/settlement/BoxOfficeReportSettlement'
import Site from 'src/models/site/Site'

const { addToDate } = date

export default class BoxOfficeReport extends Model {
  static name = 'BoxOfficeReport'

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.StudioReporting]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      site: {
        type: Site,
        output: true,
        filterable: true
      },
      movie: {
        type: Movie,
        reference: true,
        output: true,
        filterable: true
      },
      movieTerm: {
        type: MovieTerm,
        output: true,
        filterable: true
      },
      movieTermWeek: {
        type: MovieTermWeek,
        output: true
      },
      studio: {
        type: Studio,
        reference: true,
        output: true,
        filterable: true
      },
      weekOf: {
        type: IsoDate,
        output: true,
        computed () {
          if (this.startDate === this.originalData.startDate) return this.data.weekOf

          let weekOfDate = addToDate(this.startDate, { days: -4 })
          while (weekOfDate.getDay() < this.configuration.currentCircuit.dayOfWeekStart) {
            weekOfDate = addToDate(weekOfDate, { days: 1 })
          }

          return IsoDate.parse(weekOfDate)
        }
      },
      startDate: {
        type: IsoDate,
        sortable: true
      },
      endDate: {
        type: IsoDate
      },
      isPreview: {
        type: Boolean,
        output: true
      },
      weekNumber: {
        type: Integer,
        sortable: true,
        output: true
      },
      locked: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      },
      overlaps: {
        type: Boolean,
        output: true
      },
      termPercentHundredths: {
        type: Integer,
        default: 0
      },
      showingsCount: {
        type: Integer,
        default: 0,
        output: true
      },
      ticketCount: {
        type: Integer,
        default: 0,
        output: true
      },
      netCount: {
        type: Integer,
        default: 0,
        output: true
      },
      grossAmount: {
        type: Number,
        output: true,
        default: 0
      },
      netAmount: {
        type: Number,
        output: true,
        default: 0
      },
      notes: {
        type: String,
        default: null
      },
      emailBorSentAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      swiftEborSubmittedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      computedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },

      status: {
        type: String,
        output: true
      },

      hasFlatFee: {
        type: Boolean,
        default: false
      },
      flatFee: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },

      hasMinimumGuarantee: {
        type: Boolean,
        default: false
      },
      minimumGuarantee: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },

      hasRetentionFee: {
        type: Boolean,
        default: false
      },
      retentionFeePercentHundredths: {
        type: Integer,
        default: 0
      },

      depositPaid: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },

      invoiceDate: {
        type: IsoDate,
        default: null
      },
      invoiceNumber: {
        type: String,
        default: null
      },
      invoiceAmount: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      invoicePaid: {
        type: Boolean,
        default: false
      },

      retentionInvoiceDate: {
        type: IsoDate,
        default: null
      },
      retentionInvoiceNumber: {
        type: String,
        default: null
      },
      retentionInvoiceAmount: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      retentionInvoicePaid: {
        type: Boolean,
        default: false
      },

      boxOfficeReportSettlements: {
        type: [BoxOfficeReportSettlement],
        default: () => ([]),
        output: true
      },

      isAdjusting: {
        type: Boolean,
        default: false,
        local: true
      },

      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      rentalFee () {
        if (this.hasFlatFee) return this.flatFee

        if (!this.termPercentHundredths) return 0

        return this.netAmount * this.termPercentHundredths / 100 / 100
      },
      rentalDue () {
        if (this.hasFlatFee || !this.hasMinimumGuarantee) return this.rentalFee

        return Math.max(this.minimumGuarantee, this.rentalFee)
      },
      retentionDue () {
        if (!this.hasRetentionFee) return 0

        return this.rentalDue * this.retentionFeePercentHundredths / 100 / 100
      },
      amountDue () {
        return this.rentalDue - this.retentionDue
      },
      remainingDue () {
        return this.amountDue - this.depositPaid - this.invoiceAmount
      },
      remainingRetentionDue () {
        return this.retentionDue - this.retentionInvoiceAmount
      },
      isDue () {
        if (_.isNil(this.site?.siteDate)) return false

        const timeAtToday = new Date(this.site.siteDate).getTime()
        const timeAfterEndDate = addToDate(new Date(this.endDate), { days: 1 }).getTime()

        return timeAtToday >= timeAfterEndDate
      }
    }

    this.mutations = {
      updateDatesForBoxOfficeReports: {
        arguments: {
          boxOfficeReportInputs: {
            type: [this],
            required: true
          }
        },
        results: {
          boxOfficeReports: [this],
          errors: [GqlError]
        }
      }
    }

    this.paginate = true
    this.filterable = true
  }

  get shortWeekName () {
    if (this.isPreview) return `P${this.weekNumber}`

    return this.weekNumber
  }

  get weekName () {
    if (this.isPreview) return `Preview Week ${this.weekNumber}`

    return `Week ${this.weekNumber}`
  }

  get description () {
    return `${this?.movieTerm?.name || 'Unknown Term'} — ${this.weekName}`
  }

  get name () {
    return `BOR for ${this.description}`
  }

  view () {
    window.open(serverUri + '/box_office_report?id=' + this.id, '_blank')
  }

  viewSummary () {
    window.open(serverUri + '/box_office_report_summary?id=' + this.id, '_blank')
  }

  async forceRecompute () {
    this.action = 'revalidate_showings_and_data'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      return { error }
    } finally {
      this.resetField('action')
    }
  }

  async sendEmailBorSummary () {
    this.action = 'send_email_bor_summary'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      return { error }
    } finally {
      this.resetField('action')
    }
  }

  async sendEmailBor () {
    this.action = 'send_email_bor'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      return { error }
    } finally {
      this.resetField('action')
    }
  }

  async submitSwiftEbor () {
    this.action = 'submit_swift_ebor'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      return { error }
    } finally {
      this.resetField('action')
    }
  }

  async checkForNewBors () {
    this.action = 'check_for_new_bors'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      return { error }
    } finally {
      this.resetField('action')
    }
  }

  async updateDates (boxOfficeReports) {
    const results = await executeMutation(
      'updateDatesForBoxOfficeReports',
      {
        boxOfficeReportInputs: _.map(boxOfficeReports, boxOfficeReport => boxOfficeReport.deepEncode)
      },
      {
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }
}
